/**
 * @file FinanceAdminRoutes.tsx
 * @module app/routing/FinanceAdminRoutes
 * @description Defines role-specific FinanceAdminRoutes route mappings, protecting UTrade Admin screens and selecting the appropriate application views.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { lazy, FC, Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { MasterLayout } from "../../_admin/layout/MasterLayout";
import TopBarProgress from "react-topbar-progress-indicator";
import DashboardIndex from "../modules/dashboard/index";
import { getCSSVariableValue } from "../../_admin/assets/ts/_utils";
import { WithChildren } from "../../_admin/helpers";
import AdvertPage from "../modules/advertisement/index";
import PlatformFees from "../modules/platform-fees/index";
import AllProducts from "../modules/all-products/index";
import Customers from "../modules/customers/index";
import Orders from "../modules/orders-delivery/index";
import PaymentsIndex from "../modules/finance";
import OrdersDelivery from "../modules/orders-delivery/pages/finance/orders-delivery-finance";
import SettingPage from "../modules/setting";
import FinanceReport from "../modules/finance-reports/index";
import PendingDeliveryOrders from "../modules/pending-delivery-orders/PendingDeliveryOrders";
import OutstandingReport from "../modules/outstanding-report/index";

const FinanceAdminRoutes = () => {
  const SellerPage = lazy(() => import("../modules/seller/index"));
  const MasterPage = lazy(() => import("../modules/master/index"));
  const ProductPage = lazy(() => import("../modules/products/index"));
  const InventoryPage = lazy(() => import("../modules/Inventory/index"));
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route path="auth/*" element={<Navigate to="/dashboard" />} />
        {/* Pages */}
        <Route path="dashboard/*" element={<DashboardIndex />} />
        <Route
          path="finance-reports/*"
          element={
            <SuspensedView>
              <FinanceReport />
            </SuspensedView>
          }
        />
        <Route
          path="orders-delivery/*"
          element={
            <SuspensedView>
              <OrdersDelivery />
            </SuspensedView>
          }
        />
        <Route
          path="sellers/*"
          element={
            <SuspensedView>
              <SellerPage />
            </SuspensedView>
          }
        />
        <Route
          path="all-products/*"
          element={
            <SuspensedView>
              <AllProducts />
            </SuspensedView>
          }
        />
        <Route
          path="products/*"
          element={
            <SuspensedView>
              <ProductPage />
            </SuspensedView>
          }
        />
        <Route
          path="advertisement/*"
          element={
            <SuspensedView>
              <AdvertPage />
            </SuspensedView>
          }
        />
        <Route
          path="platform-fees/*"
          element={
            <SuspensedView>
              <PlatformFees />
            </SuspensedView>
          }
        />
        <Route
          path="customers/*"
          element={
            <SuspensedView>
              <Customers />
            </SuspensedView>
          }
        />
        <Route
          path="payments/*"
          element={
            <SuspensedView>
              <PaymentsIndex />
            </SuspensedView>
          }
        />
        <Route
          path="all-products/*"
          element={
            <SuspensedView>
              <AllProducts />
            </SuspensedView>
          }
        />
        <Route
          path="orders/*"
          element={
            <SuspensedView>
              <Orders />
            </SuspensedView>
          }
        />
        <Route
          path="inventory/*"
          element={
            <SuspensedView>
              <InventoryPage />
            </SuspensedView>
          }
        />
        <Route
          path="products/*"
          element={
            <SuspensedView>
              <ProductPage />
            </SuspensedView>
          }
        />
        <Route
          path="advertisement/*"
          element={
            <SuspensedView>
              <AdvertPage />
            </SuspensedView>
          }
        />
        <Route
          path="platform-fees/*"
          element={
            <SuspensedView>
              <PlatformFees />
            </SuspensedView>
          }
        />
        <Route
          path="setting/*"
          element={
            <SuspensedView>
              <SettingPage />
            </SuspensedView>
          }
        />
        <Route
          path="pending-delivery-orders/*"
          element={
            <SuspensedView>
              <PendingDeliveryOrders />
            </SuspensedView>
          }
        />
        <Route
          path="outstanding-report/*"
          element={
            <SuspensedView>
              <OutstandingReport />
            </SuspensedView>
          }
        />
      </Route>
    </Routes>
  );
};
const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue("--bs-primary");
  TopBarProgress.config({
    barColors: { "0": baseColor },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};
export { FinanceAdminRoutes };
